/**
 * Extracts visible text from the main page content area at call time.
 *
 * Targets <main>, .ant-layout-content, or [role="main"] in priority order.
 * Ant Design portals (Drawer, Modal) render outside these elements, so
 * drawer content is naturally excluded. Collapses excessive whitespace and
 * caps output so it doesn't blow up the LLM context window.
 */
export function getPageContent(maxChars = 8000): string {
  const contentEl =
    document.querySelector("#page-content") ??
    document.querySelector("main") ??
    document.querySelector(".ant-layout-content") ??
    document.querySelector("[role='main']");

  if (!contentEl) return "";

  const raw = (contentEl as HTMLElement).innerText ?? "";
  const cleaned = raw
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned.length > maxChars
    ? cleaned.slice(0, maxChars) + "\n[... page content truncated ...]"
    : cleaned;
}

// DQS data previously loaded from deleted portfolio JSON files.
// Will be wired to real API data in a future iteration.
const DQS_SITES_CONTEXT = "";
const DQS_INVESTIGATORS_CONTEXT = "";

const DQS_PLANNING_CONTEXT =
  `\n[DQS Intelligence from IQVIA (synced 2026-06-23) — use this data to answer questions about site recruitment status, competitive landscape, historical enrollment benchmarks, and investigator FDA debarment:]` +
  `\n\nSITE DQS DATA:\n${DQS_SITES_CONTEXT}` +
  `\n\nINVESTIGATOR DQS DATA:\n${DQS_INVESTIGATORS_CONTEXT}`;

interface ScreenContextEntry {
  screen: string;
  category: string | null;
  dqs?: boolean;
}

const SCREEN_CONTEXT_MAP: Record<string, ScreenContextEntry> = {
  "/home": { screen: "Trial Home", category: null },
  "/alerts": { screen: "Alerts", category: null },
  "/beta/alerts": { screen: "Alerts", category: null },
  "/insights": { screen: "Insights", category: null },
  "/beta/insights": { screen: "Insights", category: null },
  "/notifications": { screen: "Notifications", category: null },

  // Sites
  "/sites/overview": { screen: "Sites Overview", category: "Sites" },
  "/sites/enrollment": { screen: "Enrollment", category: "Sites" },
  "/sites/protocol-deviations": {
    screen: "Protocol Deviations",
    category: "Sites",
  },

  // Subjects
  "/subjects/overview": {
    screen: "Participant Profiles",
    category: "Subjects",
  },
  "/subjects/visits-activities": {
    screen: "Visits & Activities",
    category: "Subjects",
  },
  "/subjects/all-subjects": { screen: "All Subjects", category: "Subjects" },

  // Safety
  "/safety/adverse-events": { screen: "Adverse Events", category: "Safety" },
  "/safety/dosing-tracker": { screen: "Dosing Tracker", category: "Safety" },
  "/safety/ip-administration": {
    screen: "IP Administration",
    category: "Safety",
  },
  "/safety/pharmacokinetics": {
    screen: "Pharmacokinetics",
    category: "Safety",
  },

  // Measures
  "/measures/assessments": { screen: "Assessments", category: "Measures" },
  "/measures/imaging": { screen: "Imaging", category: "Measures" },
  "/measures/labs": { screen: "Labs", category: "Measures" },
  "/measures/questionnaires": {
    screen: "Questionnaires",
    category: "Measures",
  },

  // Actions (own feature, separate from the Risk section's Tasks screen)
  "/actions": { screen: "Actions", category: "Actions" },

  // Risk
  "/risk/risks": { screen: "Risks", category: "Risk" },
  "/risk/signals": { screen: "Signals", category: "Risk" },
  "/risk/issues": { screen: "Issues", category: "Risk" },
  "/risk/tasks": { screen: "Tasks", category: "Risk" },
  "/risk/analytics": { screen: "Risk Analytics", category: "Risk" },

  // Data
  "/data/configuration": { screen: "Data Configuration", category: "Data" },
  "/data/issues": { screen: "Data Issues", category: "Data" },
  "/data/trial-issues": { screen: "Trial Issues", category: "Data" },

  // Portfolio
  "/portfolio/site-profiles": {
    screen: "Site Profiles",
    category: "Portfolio",
    dqs: true,
  },
  "/portfolio/investigators": {
    screen: "Investigators",
    category: "Portfolio",
    dqs: true,
  },

  // Trial Planning
  "/planning/home": { screen: "Plan Overview", category: "Trial Planning" },
  "/sites": {
    screen: "Site & Investigator Selection",
    category: "Trial Planning",
    dqs: true,
  },
  "/milestones": { screen: "Milestones", category: "Trial Planning" },
  "/enrollment": { screen: "Enrollment Forecast", category: "Trial Planning" },
  "/vendors": { screen: "CRO & Vendors", category: "Trial Planning" },
  "/budget": { screen: "Budget", category: "Trial Planning" },
};

interface PlanningParams {
  id?: string;
  planId?: string;
}

interface ScreenContextResult {
  screenName: string | null;
  category: string | null;
  contextPrefix: string;
}

/**
 * Derives the current screen context from the URL pathname.
 *
 * @param {string} pathname - The current URL pathname
 * @param {string} trialId - The current trial ID (may be undefined in planning context)
 * @param {{ id?: string, planId?: string }} [planningParams] - Planning route params
 * @returns {{ screenName: string | null, category: string | null, contextPrefix: string }}
 */
export function getScreenContext(
  pathname: string,
  trialId: string | undefined,
  planningParams?: PlanningParams,
): ScreenContextResult {
  let routeSegment = pathname;

  if (planningParams?.id && planningParams?.planId) {
    // Planning route: /planning/:id/:planId/:tab
    routeSegment = pathname.replace(
      `/planning/${planningParams.id}/${planningParams.planId}`,
      "",
    );
  } else if (trialId) {
    routeSegment = pathname.replace(`/${trialId}`, "");
  }

  const match = SCREEN_CONTEXT_MAP[routeSegment];
  const idLabel = trialId ?? planningParams?.id ?? "unknown";

  if (match) {
    const categoryPart = match.category ? ` (under ${match.category})` : "";
    const base = `[Context: The user is currently viewing the "${match.screen}" screen${categoryPart} in the Vivo clinical trial platform for trial ${idLabel}.]`;
    const contextPrefix = match.dqs ? `${base}${DQS_PLANNING_CONTEXT}` : base;
    return {
      screenName: match.screen,
      category: match.category,
      contextPrefix,
    };
  }

  const sortedKeys = Object.keys(SCREEN_CONTEXT_MAP).sort(
    (a, b) => b.length - a.length,
  );
  for (const key of sortedKeys) {
    if (routeSegment.startsWith(key)) {
      const entry = SCREEN_CONTEXT_MAP[key];
      const categoryPart = entry.category ? ` (under ${entry.category})` : "";
      const base = `[Context: The user is currently viewing the "${entry.screen}" screen${categoryPart} in the Vivo clinical trial platform for trial ${idLabel}.]`;
      const contextPrefix = entry.dqs ? `${base}${DQS_PLANNING_CONTEXT}` : base;
      return {
        screenName: entry.screen,
        category: entry.category,
        contextPrefix,
      };
    }
  }

  return { screenName: null, category: null, contextPrefix: "" };
}
